import { Map } from "immutable";

import { Tuple } from "../Tuple";
import { ZSet } from "../ZSet";
import { insertAtPath } from "../jsonTable/insertAtPath";
import {
	type FromFilterSpec,
	type FromSpec,
	type Operator,
	emptyJoinState,
	emptySemijoinState,
	from,
	join,
	semijoin,
	where,
} from "../operatorsV2";
import { type QuerySpec, compoundQueryKey, querySpecSchema } from "../operatorsV2/QuerySpec";
import { emptyLeftOuterJoinState, leftOuterJoin } from "../operatorsV2/leftOuterJoin";
import {
	type OrderedTopKSpec,
	emptyOrderedTopKState,
	orderedTopK,
} from "../operatorsV2/orderedTopK";
import { type RunnableQueryState } from "./RunnableQueryState";

export class RunnableQuery {
	private _queryKey: string;
	private _spec: QuerySpec;
	private _permissionFilter: FromFilterSpec;
	private _finalizerSpec?: OrderedTopKSpec;
	private _query: [FromSpec, ...Operator[]];
	private _cachedParents: { tables: Set<string>; subqueries: Set<string> } | null = null;

	private constructor(
		spec: QuerySpec,
		permissionFilter: FromFilterSpec,
		finalizerSpec?: OrderedTopKSpec
	) {
		this._spec = spec;
		this._queryKey = compoundQueryKey({ querySpec: spec, permissionSpec: permissionFilter });
		this._permissionFilter = permissionFilter;
		this._finalizerSpec = finalizerSpec;

		this._query = [spec[0]];
		for (const operatorSpec of spec.slice(1)) {
			switch (operatorSpec.type) {
				case "from":
					this._query.push(operatorSpec);
					break;
				case "where": {
					const logic = where<unknown>(operatorSpec);
					this._query.push({ ...operatorSpec, logic });
					break;
				}
				case "semijoin": {
					const logic = semijoin<unknown, unknown>(operatorSpec);
					this._query.push({ ...operatorSpec, logic });
					break;
				}
				case "join": {
					const logic = join<unknown, unknown, unknown>(operatorSpec, (l, r) =>
						Tuple.from<unknown>(`${l.id} ⊕ ${r.id}`, l.derivedOwner(r), [
							l.getValue(),
							r.getValue(),
						])
					);
					this._query.push({ ...operatorSpec, logic });
					break;
				}
				case "orderedTopK": {
					const logic = orderedTopK<unknown>(operatorSpec);
					this._query.push({ ...operatorSpec, logic });
					break;
				}
				case "leftOuterJoin": {
					const logic = leftOuterJoin<unknown, unknown, unknown>(operatorSpec, (l, r) => {
						const owner = l.derivedOwner(r);
						let result: unknown;
						if (operatorSpec.output.type === "tuple") {
							result = [l.getValue(), r?.getValue()];
						} else if (operatorSpec.output.type === "insertAtPath") {
							result = insertAtPath(
								l.getValue(),
								operatorSpec.output.path.path,
								r?.getValue() ?? null
							);
						} else if (operatorSpec.output.type === "insertAggregateAtPath") {
							result = insertAtPath(
								l.getValue(),
								operatorSpec.output.path.path,
								r?.getValue() ?? null
							);
						}

						return Tuple.from<unknown>(`${l.id} ⊕ ${r?.id ?? "NULL"}`, owner, result);
					});
					this._query.push({ ...operatorSpec, logic });
					break;
				}
				default: {
					const _exhaustiveCheck: never = operatorSpec;
					throw new Error(`Unknown operator type: ${_exhaustiveCheck}`);
				}
			}
		}
		if (this._finalizerSpec) {
			this._query.push({
				type: "orderedTopK",
				orderBy: this._finalizerSpec.orderBy,
				logic: orderedTopK(this._finalizerSpec),
			});
		}
	}

	static fromSpec(
		spec: QuerySpec,
		permissionFilter?: FromFilterSpec,
		finalizerSpec?: OrderedTopKSpec
	): RunnableQuery {
		return new RunnableQuery(
			spec,
			permissionFilter ?? { type: "read_all_rows" },
			finalizerSpec
		);
	}

	static fromSpecString(
		specString: string,
		permissionFilter: FromFilterSpec,
		finalizerSpec?: OrderedTopKSpec
	): RunnableQuery {
		const spec = querySpecSchema.parse(JSON.parse(specString));
		return RunnableQuery.fromSpec(spec, permissionFilter, finalizerSpec);
	}

	get queryKey(): string {
		return this._queryKey;
	}

	get spec(): QuerySpec {
		return this._spec;
	}

	get permissionFilter(): FromFilterSpec {
		return this._permissionFilter;
	}

	get finalizerSpec(): OrderedTopKSpec | undefined {
		return this._finalizerSpec;
	}

	update(
		tableChanges: Map<string, ZSet<unknown>>,
		subqueryChanges: Map<string, ZSet<unknown>>,
		queryStates: RunnableQueryState = Map()
	): { result: ZSet<unknown>; newStates: RunnableQueryState } {
		const [fromSpec, ...plan] = this._query;
		let currentResult = tableChanges.get(fromSpec.table) ?? ZSet.empty();
		[currentResult] = from(fromSpec, this._permissionFilter)(currentResult, null);

		let currentStates = queryStates;
		for (const [specId, spec] of Object.entries(plan)) {
			switch (spec.type) {
				case "where": {
					[currentResult] = spec.logic(currentResult, null);
					break;
				}
				case "semijoin": {
					const left = currentResult;
					const subqueryId = compoundQueryKey({
						contentId: spec.right.subqueryId,
						permissionSpec: this._permissionFilter,
					});
					const right = subqueryChanges.get(subqueryId);
					if (!right) {
						throw new Error(`No subquery found with ID: ${spec.right.subqueryId}`);
					}
					const state = currentStates.get(specId) ?? emptySemijoinState();
					if (state.type !== "semijoin") {
						throw new Error(`No semijoin state found for spec: ${specId}`);
					}

					const [newResult, newState] = spec.logic(left, right, state);
					currentResult = newResult;
					currentStates = currentStates.set(specId, newState);
					break;
				}
				case "join": {
					const left = currentResult;
					const subqueryId = compoundQueryKey({
						contentId: spec.right.subqueryId,
						permissionSpec: this._permissionFilter,
					});
					const right = subqueryChanges.get(subqueryId);
					if (!right) {
						throw new Error(`No subquery found with ID: ${subqueryId}`);
					}
					const state = currentStates.get(specId) ?? emptyJoinState();
					if (state.type !== "join") {
						throw new Error(`No join state found for spec: ${specId}`);
					}

					const [newResult, newState] = spec.logic(left, right, state);
					currentResult = newResult;
					currentStates = currentStates.set(specId, newState);
					break;
				}
				case "orderedTopK": {
					const state = currentStates.get(specId) ?? emptyOrderedTopKState();
					if (state.type !== "orderedTopK") {
						throw new Error(`No orderedTopK state found for spec: ${specId}`);
					}

					const [newResult, newState] = spec.logic(currentResult, state);
					currentResult = newResult;
					currentStates = currentStates.set(specId, newState);
					break;
				}
				case "leftOuterJoin": {
					const left = currentResult;
					const subqueryKey = compoundQueryKey({
						contentId: spec.right.subqueryId,
						permissionSpec: this._permissionFilter,
					});
					const right = subqueryChanges.get(subqueryKey);
					if (!right) {
						throw new Error(`No subquery found with ID: ${subqueryKey}`);
					}
					const state = currentStates.get(specId) ?? emptyLeftOuterJoinState();
					if (state.type !== "leftOuterJoin") {
						throw new Error(`No leftOuterJoin state found for spec: ${specId}`);
					}

					const [newResult, newState] = spec.logic(left, right, state);
					currentResult = newResult;
					currentStates = currentStates.set(specId, newState);
					break;
				}
				default: {
					const _exhaustiveCheck: never = spec;
					throw new Error(`Unknown operator type: ${_exhaustiveCheck}`);
				}
			}
		}

		for (const [specId, state] of currentStates.entries()) {
			if (state.type === "semijoin") {
				currentStates = currentStates.set(specId, {
					type: "semijoin",
					keySet: state.keySet,
					rowSetByKeys: state.rowSetByKeys.map((rowSet) => rowSet),
				});
			} else if (state.type === "join") {
				currentStates = currentStates.set(specId, {
					type: "join",
					leftByK: state.leftByK.map((rowSet) => rowSet),
					rightByK: state.rightByK.map((rowSet) => rowSet),
				});
			} else if (state.type === "orderedTopK") {
				currentStates = currentStates.set(specId, {
					type: "orderedTopK",
					orderedRows: state.orderedRows,
				});
			} else if (state.type === "leftOuterJoin") {
				currentStates = currentStates.set(specId, {
					type: "leftOuterJoin",
					leftByK: state.leftByK.map((rowSet) => rowSet),
					rightByK: state.rightByK.map((rowSet) => rowSet),
					aggregateByK: state.aggregateByK.map((rowSet) => rowSet),
				});
			} else {
				const _exhaustiveCheck: never = state;
				throw new Error(`Unknown state type: ${_exhaustiveCheck}`);
			}
		}

		return { result: currentResult, newStates: currentStates };
	}

	directReferences(): { tables: Set<string>; subqueries: Set<string> } {
		if (this._cachedParents) {
			return this._cachedParents;
		}

		const tables = new Set<string>();
		const subqueries = new Set<string>();

		// Add the initial table source
		tables.add(this._query[0].table);

		// Process each operator in the plan
		for (const operator of this._query.slice(1)) {
			switch (operator.type) {
				case "from": {
					tables.add(operator.table);
					break;
				}
				case "where": {
					const { left, right } = operator.comparison;
					left.type === "jsonpath-table-ref" && tables.add(left.table);
					right.type === "jsonpath-table-ref" && tables.add(right.table);
					break;
				}
				case "semijoin": {
					const { rowKey, right } = operator;
					rowKey.type === "jsonpath-table-ref" && tables.add(operator.rowKey.table);
					right.type === "subquery" && subqueries.add(operator.right.subqueryId);
					break;
				}
				case "join": {
					const { leftKey, rightKey, right } = operator;
					leftKey.type === "jsonpath-table-ref" && tables.add(leftKey.table);
					rightKey.type === "jsonpath-table-ref" && tables.add(rightKey.table);
					right.type === "subquery" && subqueries.add(operator.right.subqueryId);
					break;
				}
				case "orderedTopK": {
					const { orderBy } = operator;
					orderBy?.type === "jsonpath-table-ref" && tables.add(orderBy.table);
					break;
				}
				case "leftOuterJoin": {
					const { leftKey, rightKey, right } = operator;
					leftKey.type === "jsonpath-table-ref" && tables.add(leftKey.table);
					rightKey.type === "jsonpath-table-ref" && tables.add(rightKey.table);
					right.type === "subquery" && subqueries.add(operator.right.subqueryId);
					break;
				}
				default: {
					const _exhaustiveCheck: never = operator;
					throw new Error(`Unknown operator type: ${_exhaustiveCheck}`);
				}
			}
		}

		this._cachedParents = { tables, subqueries };
		return this._cachedParents;
	}
}
